import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import SafeAnimatePresence from "./SafeAnimatePresence";
import {
  FiCompass, FiSearch, FiX, FiArrowLeft, FiCheck, FiMessageSquare, FiEyeOff, FiSun, FiMoon,
} from "react-icons/fi";
import { CENTERS, PINNED_WORKSPACES, centerPath, isWorkspaceEnabled, type CenterWorkspace } from "../../constants/centers";
import { useTheme } from "../../context/ThemeContext";
import {
  getCompletedTasks, markTaskDone, getVisitedCenters, type WalkthroughTaskId,
} from "../../features/quickActions/walkthroughProgress";
import { WALKTHROUGH_GROUPS, TOTAL_WALKTHROUGH_TASKS } from "../../features/quickActions/walkthroughTasks";
import "../../styles/QuickActionsPalette.css";

type QAItem =
  | { kind: "center"; key: string; name: string; blurb: string; icon: React.ReactNode; path: string }
  | { kind: "workspace"; key: string; name: string; blurb: string; icon: React.ReactNode; path: string }
  | { kind: "command"; key: string; name: string; blurb: string; icon: React.ReactNode; run: () => void };

const TYPE_LABEL: Record<QAItem["kind"], string> = {
  center: "Center",
  workspace: "Workspace",
  command: "Command",
};

interface QuickActionsPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  openedViaKeyboard: boolean;
  hiddenMenuItems: Set<string>;
  onNewChat: () => void;
  onShowHiddenWorkspaces: () => void;
  /** Lets a workspace/center pick also dismiss the mobile sidebar drawer, same as every other nav row. */
  onAfterNavigate?: () => void;
}

const QuickActionsPalette: React.FC<QuickActionsPaletteProps> = ({
  isOpen,
  onClose,
  openedViaKeyboard,
  hiddenMenuItems,
  onNewChat,
  onShowHiddenWorkspaces,
  onAfterNavigate,
}) => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [view, setView] = useState<"palette" | "walkthrough">("palette");
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [completed, setCompleted] = useState<Set<WalkthroughTaskId>>(() => getCompletedTasks());
  const inputRef = useRef<HTMLInputElement>(null);

  const complete = useCallback((id: WalkthroughTaskId) => {
    if (markTaskDone(id)) setCompleted(getCompletedTasks());
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setActiveIndex(0);
    setView("palette");
    setCompleted(getCompletedTasks());
    if (openedViaKeyboard) complete("opened-with-shortcut");
    if (hiddenMenuItems.size > 0) complete("hid-a-workspace");
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(focusTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const navItems: QAItem[] = useMemo(() => {
    const items: QAItem[] = [];
    PINNED_WORKSPACES
      .filter(w => isWorkspaceEnabled(w) && !hiddenMenuItems.has(w.path))
      .forEach(w => items.push({ kind: "workspace", key: w.path, name: w.name, blurb: w.blurb, icon: w.icon, path: w.path }));
    CENTERS.forEach(center => {
      items.push({ kind: "center", key: `center:${center.id}`, name: center.name, blurb: center.blurb, icon: center.icon, path: centerPath(center.id) });
      center.workspaces
        .filter((w: CenterWorkspace) => isWorkspaceEnabled(w) && !hiddenMenuItems.has(w.path))
        .forEach(w => items.push({ kind: "workspace", key: w.path, name: w.name, blurb: `${center.shortName} · ${w.blurb}`, icon: w.icon, path: w.path }));
    });
    return items;
  }, [hiddenMenuItems]);

  const commandItems: QAItem[] = useMemo(() => {
    const items: QAItem[] = [
      {
        kind: "command", key: "new-chat", name: "New chat", blurb: "Start a fresh conversation",
        icon: <FiMessageSquare size={14} />, run: onNewChat,
      },
      {
        kind: "command", key: "toggle-theme", name: theme === "dark" ? "Switch to light theme" : "Switch to dark theme",
        blurb: "Change the interface theme", icon: theme === "dark" ? <FiSun size={14} /> : <FiMoon size={14} />, run: toggleTheme,
      },
    ];
    if (hiddenMenuItems.size > 0) {
      items.push({
        kind: "command", key: "show-hidden", name: "Show hidden workspaces",
        blurb: `${hiddenMenuItems.size} hidden from your nav`, icon: <FiEyeOff size={14} />, run: onShowHiddenWorkspaces,
      });
    }
    return items;
  }, [theme, toggleTheme, hiddenMenuItems, onNewChat, onShowHiddenWorkspaces]);

  const trimmedQuery = query.trim().toLowerCase();
  const isSearching = trimmedQuery.length > 0;

  const filteredNavItems = useMemo(() => {
    if (!isSearching) return navItems;
    return navItems.filter(item => item.name.toLowerCase().includes(trimmedQuery) || item.blurb.toLowerCase().includes(trimmedQuery));
  }, [navItems, isSearching, trimmedQuery]);

  const filteredCommandItems = useMemo(() => {
    if (!isSearching) return commandItems;
    return commandItems.filter(item => item.name.toLowerCase().includes(trimmedQuery) || item.blurb.toLowerCase().includes(trimmedQuery));
  }, [commandItems, isSearching, trimmedQuery]);

  const flatList = useMemo(() => [...filteredNavItems, ...filteredCommandItems], [filteredNavItems, filteredCommandItems]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, view]);

  const selectItem = useCallback((item: QAItem) => {
    if (isSearching) complete("searched-by-typing");
    complete("jumped-to-something");
    if (item.kind === "command") {
      item.run();
    } else {
      navigate(item.path);
      onAfterNavigate?.();
    }
    onClose();
  }, [isSearching, complete, navigate, onAfterNavigate, onClose]);

  const goToWalkthrough = useCallback(() => {
    setQuery("");
    setView("walkthrough");
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const runWalkthroughAction = useCallback((taskId: WalkthroughTaskId) => {
    if (taskId === "asked-ai") {
      onNewChat();
      complete("asked-ai");
      onClose();
    } else if (taskId === "hid-a-workspace") {
      onShowHiddenWorkspaces();
      onClose();
    } else if (taskId === "visited-every-center") {
      const visited = getVisitedCenters();
      const next = CENTERS.find(c => !visited.has(c.id)) ?? CENTERS[0];
      navigate(centerPath(next.id));
      onAfterNavigate?.();
      onClose();
    }
  }, [onNewChat, complete, onClose, onShowHiddenWorkspaces, navigate, onAfterNavigate]);

  const filteredWalkthroughGroups = useMemo(() => {
    if (!isSearching) return WALKTHROUGH_GROUPS;
    return WALKTHROUGH_GROUPS
      .map(g => ({ ...g, tasks: g.tasks.filter(t => t.label.toLowerCase().includes(trimmedQuery)) }))
      .filter(g => g.tasks.length > 0);
  }, [isSearching, trimmedQuery]);

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (view === "palette") {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex(i => (flatList.length ? (i + 1) % flatList.length : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex(i => (flatList.length ? (i - 1 + flatList.length) % flatList.length : 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flatList[activeIndex];
        if (item) selectItem(item);
      } else if (e.key === "Escape") {
        onClose();
      }
    } else {
      if (e.key === "Escape") {
        e.preventDefault();
        setQuery("");
        setView("palette");
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  const completedCount = completed.size;
  const activeItemKey = flatList[activeIndex]?.key;

  const renderItemRow = (item: QAItem, index: number) => (
    <div
      key={item.key}
      id={`qa-option-${index}`}
      role="option"
      aria-selected={activeItemKey === item.key}
      className={`qa-result-item ${activeItemKey === item.key ? "qa-result-item--active" : ""}`}
      onMouseEnter={() => setActiveIndex(index)}
      onClick={() => selectItem(item)}
    >
      <span className="qa-result-icon">{item.icon}</span>
      <div className="qa-result-info">
        <div className="qa-result-title">{item.name}</div>
        <div className="qa-result-meta">{item.blurb}</div>
      </div>
      <span className="qa-result-tag">{TYPE_LABEL[item.kind]}</span>
    </div>
  );

  return (
    <SafeAnimatePresence>
      {isOpen && (
        <motion.div
          className="qa-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            className="qa-palette glass-surface"
            initial={{ opacity: 0, y: -16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.97 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="qa-header">
              {view === "walkthrough" && (
                <button
                  type="button"
                  className="qa-back"
                  onClick={() => { setQuery(""); setView("palette"); setTimeout(() => inputRef.current?.focus(), 0); }}
                  aria-label="Back to Quick Actions"
                >
                  <FiArrowLeft size={16} />
                </button>
              )}
              <div className="qa-input-wrap">
                <FiSearch size={16} className="qa-input-icon" />
                <input
                  ref={inputRef}
                  className="qa-input"
                  role="combobox"
                  aria-expanded={isOpen}
                  aria-controls="qa-listbox"
                  aria-activedescendant={view === "palette" ? `qa-option-${activeIndex}` : undefined}
                  placeholder={view === "walkthrough" ? "Search tasks to become a Momentum AMP expert..." : "Search Centers, workspaces, and commands..."}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                />
              </div>
              <button
                type="button"
                className="qa-ask-ai"
                onClick={() => runWalkthroughAction("asked-ai")}
                title="Ask AI"
              >
                <FiMessageSquare size={13} />
                <span>Ask AI</span>
              </button>
              <button type="button" className="qa-close" onClick={onClose} aria-label="Close Quick Actions">
                <FiX size={18} />
              </button>
            </div>

            {view === "palette" ? (
              <div className="qa-results" id="qa-listbox" role="listbox">
                {!isSearching && (
                  <div
                    className="qa-walkthrough-teaser"
                    role="button"
                    tabIndex={0}
                    onClick={goToWalkthrough}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToWalkthrough(); } }}
                  >
                    <span className="qa-walkthrough-teaser__icon"><FiCompass size={14} /></span>
                    <span className="qa-walkthrough-teaser__label">Get the most out of Momentum AMP</span>
                    <span className="qa-walkthrough-teaser__progress">{completedCount}/{TOTAL_WALKTHROUGH_TASKS} completed</span>
                    <span className="qa-walkthrough-teaser__cta">Walkthrough</span>
                  </div>
                )}

                {flatList.length === 0 && (
                  <div className="qa-empty">No matches for "{query}" — try a Center or workspace name</div>
                )}

                {filteredNavItems.length > 0 && (
                  <div className="qa-section">
                    {!isSearching && <div className="qa-section-label">Centers &amp; workspaces</div>}
                    {filteredNavItems.map((item) => renderItemRow(item, flatList.indexOf(item)))}
                  </div>
                )}

                {filteredCommandItems.length > 0 && (
                  <div className="qa-section">
                    {!isSearching && <div className="qa-section-label">Commands</div>}
                    {filteredCommandItems.map((item) => renderItemRow(item, flatList.indexOf(item)))}
                  </div>
                )}
              </div>
            ) : (
              <div className="qa-walkthrough">
                <div className="qa-walkthrough__intro">
                  <div className="qa-walkthrough__title">Welcome to Momentum AMP!</div>
                  <div className="qa-walkthrough__subtitle">Keep going — every task teaches you something you'll use again.</div>
                  <div className="qa-walkthrough__count">{completedCount} of {TOTAL_WALKTHROUGH_TASKS} completed</div>
                  <div className="qa-walkthrough__bar">
                    <div
                      className="qa-walkthrough__bar-fill"
                      style={{ "--qa-progress": completedCount / TOTAL_WALKTHROUGH_TASKS } as React.CSSProperties}
                    />
                  </div>
                </div>

                {filteredWalkthroughGroups.length === 0 && (
                  <div className="qa-empty">No tasks match "{query}"</div>
                )}

                {filteredWalkthroughGroups.map(group => (
                  <div key={group.label} className="qa-section">
                    <div className="qa-section-label">{group.label} · {group.tasks.length} task{group.tasks.length === 1 ? "" : "s"}</div>
                    {group.tasks.map(task => {
                      const done = completed.has(task.id);
                      return (
                        <div
                          key={task.id}
                          className={`qa-task ${done ? "qa-task--done" : ""} ${task.action ? "qa-task--actionable" : ""}`}
                          role={task.action ? "button" : undefined}
                          tabIndex={task.action ? 0 : undefined}
                          onClick={() => task.action && runWalkthroughAction(task.id)}
                          onKeyDown={(e) => {
                            if (task.action && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); runWalkthroughAction(task.id); }
                          }}
                        >
                          <span className="qa-task__icon">{task.icon}</span>
                          <span className="qa-task__label">{task.label}</span>
                          {task.action && !done && <span className="qa-task__action">{task.action.label}</span>}
                          <span className={`qa-task__status ${done ? "qa-task__status--done" : ""}`}>
                            {done ? <FiCheck size={11} /> : null}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            <div className="qa-footer">
              <span><kbd>↑</kbd><kbd>↓</kbd> Navigate</span>
              <span><kbd>↵</kbd> Select</span>
              <span><kbd>Esc</kbd> {view === "walkthrough" ? "Back" : "Close"}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </SafeAnimatePresence>
  );
};

export default QuickActionsPalette;
